import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../../axios/axiosInstance';
import { Country, State, District, Taluk, City, EntityType } from '../types';

interface LocationFormProps {
  entityType: EntityType;
  onClose: () => void;
  onSuccess: () => void;
  editData?: Country | State | District | Taluk | City | null;
  parentOptions?: { id: number; name: string }[];
  parentLabel?: string;
}

const LocationForm: React.FC<LocationFormProps> = ({
  entityType,
  onClose,
  onSuccess,
  editData,
  parentOptions = [],
  parentLabel = '',
}) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    ...(entityType !== 'country' && { [`${entityType === 'state' ? 'country' : entityType === 'district' ? 'state' : entityType === 'taluk' ? 'district' : 'taluk'}_id`]: '' }),
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editData) {
      setFormData({
        name: editData.name || '',
        code: editData.code || '',
        ...(entityType !== 'country' && {
          [`${entityType === 'state' ? 'country' : entityType === 'district' ? 'state' : entityType === 'taluk' ? 'district' : 'taluk'}_id`]: 
            entityType === 'state' ? (editData as State).country_id :
            entityType === 'district' ? (editData as District).state_id :
            entityType === 'taluk' ? (editData as Taluk).district_id :
            (editData as City).taluk_id
        }),
      });
    }
  }, [editData, entityType]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (entityType !== 'country' && !formData[`${entityType === 'state' ? 'country' : entityType === 'district' ? 'state' : entityType === 'taluk' ? 'district' : 'taluk'}_id` as keyof typeof formData]) {
      toast.error(`${parentLabel} is required`);
      return;
    }

    setLoading(true);
    try {
      const payload: any = {
        name: formData.name.trim(),
        ...(formData.code.trim() && { code: formData.code.trim() }),
      };

      if (entityType !== 'country') {
        const parentKey = `${entityType === 'state' ? 'country' : entityType === 'district' ? 'state' : entityType === 'taluk' ? 'district' : 'taluk'}_id`;
        payload[parentKey] = Number(formData[parentKey as keyof typeof formData]);
      }

      if (editData) {
        await axiosInstance.put(`/api/${entityType}/${editData.id}`, payload);
        toast.success(`${entityType.charAt(0).toUpperCase() + entityType.slice(1)} updated successfully`);
      } else {
        await axiosInstance.post(`/api/${entityType}`, payload);
        toast.success(`${entityType.charAt(0).toUpperCase() + entityType.slice(1)} created successfully`);
      }
      
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || `Failed to ${editData ? 'update' : 'create'} ${entityType}`);
    } finally {
      setLoading(false);
    }
  };

  const entityLabel = entityType.charAt(0).toUpperCase() + entityType.slice(1);

  return (
    <div className="p-6 w-full">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          {editData ? `Edit ${entityLabel}` : `Add ${entityLabel}`}
        </h2>
        <button
          onClick={onClose}
          className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          aria-label="Close"
        >
          <i className="fa-solid fa-times text-xl"></i>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {entityType !== 'country' && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {parentLabel} <span className="text-red-500">*</span>
            </label>
            <select
              value={formData[`${entityType === 'state' ? 'country' : entityType === 'district' ? 'state' : entityType === 'taluk' ? 'district' : 'taluk'}_id` as keyof typeof formData] as string}
              onChange={(e) => handleChange(`${entityType === 'state' ? 'country' : entityType === 'district' ? 'state' : entityType === 'taluk' ? 'district' : 'taluk'}_id`, e.target.value)}
              className="form-select w-full"
              required
            >
              <option value="">Select {parentLabel}</option>
              {parentOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className="form-input w-full"
            required
            placeholder={`Enter ${entityLabel} name`}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Code <span className="text-gray-500 text-xs">(Optional)</span>
          </label>
          <input
            type="text"
            value={formData.code}
            onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
            className="form-input w-full"
            placeholder={`Enter ${entityLabel} code`}
            maxLength={10}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="btn-outline px-6 py-2.5"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary px-6 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <i className="fa-solid fa-spinner fa-spin"></i>
                <span>{editData ? 'Updating...' : 'Creating...'}</span>
              </>
            ) : (
              editData ? 'Update' : 'Create'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LocationForm;

